import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Product, ProductWithDiscount } from "@/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price: number) {
  return price.toLocaleString("ko-KR") + "원";
}

// All display formatting goes through Asia/Seoul. The DB still stores
// UTC instants (Postgres timestamp), but anywhere a user-facing string is
// produced we anchor to KST so server timezone doesn't bleed through.
const KST_TIMEZONE = "Asia/Seoul";

// KST wall-clock components for an instant. Timezone-independent: the same
// instant formats identically on a UTC server and in a Seoul browser.
function kstParts(d: Date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: KST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const o = Object.fromEntries(parts.map((p) => [p.type, p.value])) as Record<
    string,
    string
  >;
  // Some engines emit "24" for midnight under hour12:false.
  if (o.hour === "24") o.hour = "00";
  return o;
}

// Assembles a KST-anchored string directly from Intl parts, so display
// formatting is identical regardless of the runtime's local timezone.
export function formatKst(date: Date, pattern: string): string {
  const o = kstParts(date);
  const map: Record<string, string> = {
    yyyy: o.year,
    MM: o.month,
    dd: o.day,
    HH: o.hour,
    mm: o.minute,
    ss: o.second,
  };
  return pattern.replace(/yyyy|MM|dd|HH|mm|ss/g, (t) => map[t]);
}

export function formatDate(date: Date | string, pattern = "yyyy년 MM월 dd일") {
  return formatKst(new Date(date), pattern);
}

export function formatDateTime(date: Date | string) {
  return formatKst(new Date(date), "yyyy.MM.dd HH:mm");
}

// `YYYY-MM-DDTHH:mm` for <input type="datetime-local">; KST wall-clock.
export function toKstDatetimeLocal(date: Date | string): string {
  return formatKst(new Date(date), "yyyy-MM-ddTHH:mm");
}

// Inverse: a "YYYY-MM-DDTHH:mm" string the user typed in a KST-displayed
// datetime-local input → proper UTC ISO instant for the API.
export function kstDatetimeLocalToUtcIso(value: string): string {
  const [date, time] = value.split("T");
  const t = (time ?? "00:00").length === 5 ? `${time}:00` : time;
  return new Date(`${date}T${t}+09:00`).toISOString();
}

// `YYYY-MM-DD` in KST (e.g. for CSV filenames, today checks)
export function kstDateString(date: Date | string = new Date()): string {
  return formatKst(new Date(date), "yyyy-MM-dd");
}

// KST year, used by Footer copyright etc.
export function kstYear(date: Date | string = new Date()): number {
  return Number(formatKst(new Date(date), "yyyy"));
}

export function getFinalPrice(product: Product): number {
  if (!isProductOnDiscount(product)) return product.price;
  return Math.floor(product.price * (1 - product.discountRate / 100));
}

// 상품 할인율을 임의의 기준가(옵션 가격 등)에 적용한다.
export function applyProductDiscount(
  product: Pick<Product, "discountRate" | "discountStartAt" | "discountEndAt">,
  basePrice: number,
): number {
  if (!isProductOnDiscount(product as Product)) return basePrice;
  return Math.floor(basePrice * (1 - product.discountRate / 100));
}

export function isProductOnDiscount(product: Product): boolean {
  if (product.discountRate === 0) return false;
  const now = new Date();
  if (product.discountStartAt && new Date(product.discountStartAt) > now)
    return false;
  if (product.discountEndAt && new Date(product.discountEndAt) < now)
    return false;
  return true;
}

export function getProductWithDiscount(product: Product): ProductWithDiscount {
  const isOnDiscount = isProductOnDiscount(product);
  const productFinalPrice = applyProductDiscount(product, product.price);

  // 단위 옵션에 각각 할인가를 얹고 sortOrder대로 정렬한다.
  const options = [...(product.options ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((o) => ({
      ...o,
      finalPrice: applyProductDiscount(product, o.price),
    }));
  const hasOptions = options.length > 0;
  // 옵션이 2개 이상일 때만 "선택"이 의미가 있다. 1개면 단일 단위처럼 취급.
  const hasMultipleOptions = options.length >= 2;
  const sellable = options.filter((o) => o.isAvailable);
  const priceBasis = sellable.length > 0 ? sellable : options;
  // 대표가로 쓸 "가장 싼" 옵션. 가격과 단위 라벨을 한 옵션에서 함께 뽑아
  // "10kg 30,000원~"처럼 가격과 단위가 일치하도록 한다.
  const cheapest = hasOptions
    ? priceBasis.reduce((a, b) => (b.finalPrice < a.finalPrice ? b : a))
    : null;

  // 예약 상품은 재고와 무관하게 순차 발송으로 판매되므로 최소 주문 수량
  // 미달을 이유로 품절 처리하지 않는다. 그 외 단일 단위 상품은 재고가
  // 최소 주문 수량에 못 미치면 사실상 판매 불가이므로 품절로 취급한다.
  const belowMinimumOrderQuantity =
    !product.isPreorder && product.stock < product.minOrderQuantity;

  return {
    ...product,
    options,
    hasOptions,
    hasMultipleOptions,
    // 카드/상세 대표가: 옵션이 있으면 최저가 옵션 가격, 없으면 상품가.
    finalPrice: cheapest ? cheapest.finalPrice : productFinalPrice,
    // 대표가에 해당하는 단위 라벨(옵션 없으면 상품 단위).
    startingUnit: cheapest ? cheapest.label : product.unit,
    isOnDiscount,
    // 재고 판단: 옵션이 있으면 판매중 옵션의 최대 재고, 없으면 상품 재고
    // (단, 최소 주문 수량에 못 미치면 0으로 취급).
    effectiveStock: hasOptions
      ? Math.max(0, ...sellable.map((o) => o.stock))
      : belowMinimumOrderQuantity
        ? 0
        : product.stock,
  };
}

// Google requires the *total* price for the minimum purchasable quantity,
// not a per-unit price, whenever a minimum order quantity applies
// (support.google.com/merchants/answer/12159029). Every place that renders
// a product price in structured data — the Google Merchant Center feed
// (src/app/api/google-merchant-feed/route.ts) and the product page's own
// JSON-LD (src/app/(public)/products/[id]/page.tsx) — must agree on this
// number, since Google crawls both and a mismatch is exactly what triggers
// price-mismatch warnings. Both call this instead of multiplying inline so
// the two can't drift apart silently.
export function priceForMinimumOrderQuantity(
  unitPrice: number,
  minOrderQuantity: number,
): number {
  return unitPrice * minOrderQuantity;
}

/**
 * 유니코드 코드 포인트 단위로 문자열을 자른다.
 *
 * `String.prototype.slice`는 UTF-16 코드 유닛 단위로 잘라, 그 경계가 서로게이트
 * 쌍(이모지, 일부 한글·한자) 한가운데를 지나면 짝 잃은 서로게이트가 남는다.
 * DB에 그대로 쓰면 잘못된 UTF-8로 거부된다.
 */
export function clampCodePoints(value: string, max: number): string {
  return [...value].slice(0, max).join("");
}

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

export function phoneFormat(phone: string) {
  return phone.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
}

export function getImageUrl(
  path: string | null | undefined,
  fallback = "/images/placeholder.jpg",
) {
  if (!path) return fallback;
  if (path.startsWith("http")) return path;
  return `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/farm-images/${path}`;
}

// KST boundary helpers — used by admin dashboard/reports to compute
// "today/month" ranges that are correct regardless of the server's TZ.
function kstYmdParts(d: Date): { year: number; month: number; day: number } {
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: KST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  const [y, m, day] = ymd.split("-").map((s) => parseInt(s, 10));
  return { year: y, month: m, day };
}

export function kstStartOfToday(now: Date = new Date()): Date {
  const { year, month, day } = kstYmdParts(now);
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return new Date(`${year}-${mm}-${dd}T00:00:00+09:00`);
}

export function kstStartOfMonth(now: Date = new Date()): Date {
  const { year, month } = kstYmdParts(now);
  const mm = String(month).padStart(2, "0");
  return new Date(`${year}-${mm}-01T00:00:00+09:00`);
}

export function siteUrl(): string {
  return (
    process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, "") ??
    "https://xn--py1b268cz5b.com"
  );
}
